import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { RoundedBox } from '@react-three/drei'
import { HAL, useIndeling } from './indeling'
import { useAnimatie, useMaterialen, useObjecten } from '../scene/store'
import { useMerkTokens } from '../kiosk/useMerkTokens'
import { gangpadBord, nooduitgang } from './etiketten'
import { Palletpendel } from './Palletpendel'
import { Contactschaduw } from './trucks/Contactschaduw'
import { halklok, rondHz, rondOm } from './omloop'

/**
 * Wat hier draait, rijdt en knippert loopt op de klok van de hal, met
 * frequenties die een geheel aantal keer rondgaan per omloop (`omloop.ts`).
 */
const WIKKELTAFEL = rondOm(1.9)
const WIKKELARM = rondOm(0.85)
/** De schrobmachine: een rondje van ±52 s, vier per omloop. */
const SCHROB = rondOm(0.12)
const ZWAAILICHT = rondOm(3.4)
/** De balk in een display springt vijf standen door, 0,4 per seconde. */
const DISPLAY = rondHz(0.4, 5)
const NOODUITGANG = rondOm(0.9)

/**
 * Het natte spoor achter de schrobmachine loopt uit: vol bij de zuigmond,
 * weg na twee meter. Een vlak met overal dezelfde dekking leest als een mat.
 */
function natSpoorKaart() {
  const n = 64
  const data = new Uint8Array(n * 4)
  for (let i = 0; i < n; i++) {
    // Het vlak ligt plat met zijn v-as naar achteren: v = 0 is de zuigmond.
    const v = i / (n - 1)
    const a = Math.pow(1 - v, 1.4)
    data.fill(Math.round(a * 255), i * 4, i * 4 + 4)
  }
  const t = new THREE.DataTexture(data, 1, n)
  t.needsUpdate = true
  return t
}

/**
 * Installaties en bebording: de dingen die een hal tot een gebouw maken in
 * plaats van tot een verzameling stellingen. Wikkelaar, gangpadborden,
 * nooduitgangen, mezzanine, veiligheidsspiegels, displayborden, stillages,
 * de C-MATIC-palletpendel en een schrobmachine.
 *
 * `machines`: de wikkelaar, de pendel en de schrobmachine; uit zolang de
 * machines uit staan (`Magazijn`).
 */
export function Installaties({ machines = true }: { machines?: boolean }) {
  const ind = useIndeling()
  const obj = useObjecten()
  const anim = useAnimatie()
  const mat = useMaterialen()
  const tokens = useMerkTokens()

  const wikkelTafel = useRef<THREE.Group>(null)
  const wikkelArm = useRef<THREE.Group>(null)
  const schrobber = useRef<THREE.Group>(null)
  const zwaailicht = useRef<THREE.Mesh>(null)
  const displays = useRef<THREE.Group>(null)
  const exits = useRef<THREE.Group>(null)

  const rand = ind.blokDiepte + 2.6
  const kruising = ind.blokDiepte + 6

  // Borden met echte tekst: "GANG 03" in plaats van een witte balk, en het
  // groene vluchtwegbord met de rennende figuur.
  const borden = useMemo(
    () => ({
      gang: Array.from({ length: ind.cfg.gangpaden }, (_, i) => gangpadBord(i + 1)),
      exit: nooduitgang(),
    }),
    [ind.cfg.gangpaden],
  )
  useEffect(() => () => { borden.gang.forEach((b) => b.dispose()); borden.exit.dispose() }, [borden])

  const natSpoor = useMemo(natSpoorKaart, [])
  useEffect(() => () => natSpoor.dispose(), [natSpoor])

  // Displaywaarden die periodiek verspringen
  const cijfers = useMemo(
    () => Array.from({ length: 3 }, (_, i) => 120 + i * 37),
    [],
  )

  useFrame(() => {
    const t = halklok.t

    // Wikkelaar: draaitafel en folie-arm die op en neer gaat
    if (wikkelTafel.current && anim.wikkelaar) {
      wikkelTafel.current.rotation.y = t * WIKKELTAFEL
    }
    if (wikkelArm.current && anim.wikkelaar) {
      wikkelArm.current.position.y = 0.6 + (Math.sin(t * WIKKELARM) * 0.5 + 0.5) * 1.5
    }

    // Schrobmachine maakt trage rondjes in het vrije vak. Zijn neus (lokaal
    // +z, de borstelkap) volgt de raaklijn van de ellips; eerst wees hij naar
    // buiten en reed hij zijwaarts.
    if (schrobber.current && anim.aan) {
      const a = t * SCHROB
      schrobber.current.position.x = 14 + Math.cos(a) * 7
      schrobber.current.position.z = kruising + 4 + Math.sin(a) * 4
      schrobber.current.rotation.y = Math.atan2(-7 * Math.sin(a), 4 * Math.cos(a))
    }

    // Zwaailicht bij de kruising draait rond
    if (zwaailicht.current && anim.zwaailicht) {
      zwaailicht.current.rotation.y = t * ZWAAILICHT
    }

    // Displayborden: de balk erin verspringt
    if (displays.current && anim.displays) {
      displays.current.children.forEach((d, i) => {
        const balk = d.children[1] as THREE.Mesh | undefined
        if (!balk) return
        const stap = Math.floor(t * DISPLAY + i) % 5
        balk.scale.x = 0.25 + stap * 0.18
      })
    }

    // Nooduitgangen ademen heel licht — leest als echte noodverlichting
    if (exits.current) {
      exits.current.children.forEach((e, i) => {
        const m = (e as THREE.Mesh).material as THREE.MeshBasicMaterial
        m.opacity = 0.75 + Math.sin(t * NOODUITGANG + i) * 0.1
      })
    }
  })

  return (
    <group>
      {/* ---- Stretchwikkelaar met draaitafel ---- */}
      {machines && obj.wikkelaar && (
        <group position={[HAL.breedte * 0.2, 0, rand + 5]}>
          <mesh position={[0, 0.1, 0]} receiveShadow>
            <cylinderGeometry args={[1.05, 1.05, 0.2, 24]} />
            <meshStandardMaterial color="#3b424b" roughness={0.5} metalness={0.6} />
          </mesh>
          <group ref={wikkelTafel} position={[0, 0.22, 0]}>
            <mesh position={[0, 0.07, 0]} castShadow>
              <boxGeometry args={[1.2, 0.14, 0.8]} />
              <meshStandardMaterial color={mat.pallet} roughness={0.9} />
            </mesh>
            <mesh position={[0, 0.72, 0]} castShadow>
              <boxGeometry args={[1.0, 1.15, 0.7]} />
              <meshStandardMaterial color="#b9c4cc" roughness={0.35} transparent opacity={0.75} />
            </mesh>
          </group>
          {/* Mast met folie-arm */}
          <mesh position={[1.45, 1.4, 0]} castShadow>
            <boxGeometry args={[0.22, 2.8, 0.22]} />
            <meshStandardMaterial color="#4a525c" roughness={0.45} metalness={0.65} />
          </mesh>
          <group ref={wikkelArm} position={[1.1, 0.6, 0]}>
            <mesh>
              <boxGeometry args={[0.5, 0.2, 0.28]} />
              <meshStandardMaterial color={tokens.hi} roughness={0.5} />
            </mesh>
            <mesh position={[-0.18, 0, 0]}>
              <cylinderGeometry args={[0.1, 0.1, 0.34, 10]} />
              <meshStandardMaterial color="#d6dde2" roughness={0.3} />
            </mesh>
          </group>
        </group>
      )}

      {/* ---- Gangpadborden aan het plafond ---- */}
      {obj.gangpadborden &&
        Array.from({ length: ind.cfg.gangpaden }).map((_, i) => (
          <group key={i} position={[HAL.breedte * 0.36, HAL.hoogte - 2.6, ind.gangpadZ(i)]}>
            <mesh>
              <boxGeometry args={[0.06, 0.62, 1.5]} />
              <meshStandardMaterial color="#1f4f86" roughness={0.6} />
            </mesh>
            {/* Het nummer, aan beide kanten: je rijdt het gangpad van twee kanten in. */}
            {[-1, 1].map((kant) => (
              <mesh key={kant} position={[kant * 0.032, 0, 0]} rotation={[0, (kant * Math.PI) / 2, 0]}>
                <planeGeometry args={[1.46, 0.6]} />
                <meshStandardMaterial map={borden.gang[i]} roughness={0.5} emissiveMap={borden.gang[i]} emissive="#ffffff" emissiveIntensity={0.08} />
              </mesh>
            ))}
            {[-0.5, 0.5].map((dz) => (
              <mesh key={dz} position={[0, 0.6, dz]}>
                <cylinderGeometry args={[0.012, 0.012, 0.6, 6]} />
                <meshStandardMaterial color="#5a626c" metalness={0.6} roughness={0.5} />
              </mesh>
            ))}
          </group>
        ))}

      {/* ---- Nooduitgangborden: klein, groen, en ze maken het een gebouw ---- */}
      {obj.nooduitgangen && (
        <group ref={exits}>
          {([[-HAL.breedte * 0.46, 3.2, kruising], [HAL.breedte * 0.46, 3.2, kruising],
             [-HAL.breedte * 0.46, 3.2, -kruising], [HAL.breedte * 0.46, 3.2, -kruising]] as const).map(
            ([x, y, z], i) => (
              // Verlicht: een vluchtwegbord brandt altijd, dus HDR, zodat het in
              // de verte een gloed krijgt zoals een echt noodbord.
              <mesh key={i} position={[x, y, z]} rotation={[0, x < 0 ? Math.PI / 2 : -Math.PI / 2, 0]}>
                <planeGeometry args={[0.66, 0.3]} />
                <meshBasicMaterial map={borden.exit} color={[2.2, 2.2, 2.2]} toneMapped={false} side={THREE.DoubleSide} />
              </mesh>
            ),
          )}
        </group>
      )}

      {/* ---- Mezzanine met verlicht kantoor: schaalreferentie ---- */}
      {obj.mezzanine && (
        <group position={[-HAL.breedte * 0.34, 0, -kruising - 3]}>
          <mesh position={[0, 3.1, 0]} castShadow receiveShadow>
            <boxGeometry args={[9, 0.24, 6]} />
            <meshStandardMaterial color="#3a414a" roughness={0.6} metalness={0.4} />
          </mesh>
          {/* Kantoorbox erop, met licht erin */}
          <mesh position={[0, 4.6, 0]} castShadow>
            <boxGeometry args={[7, 2.6, 4.6]} />
            <meshStandardMaterial color="#2b313a" roughness={0.7} />
          </mesh>
          {[-2.2, 0, 2.2].map((x) => (
            <mesh key={x} position={[x, 4.8, 2.32]}>
              <planeGeometry args={[1.6, 1.1]} />
              <meshBasicMaterial color="#c9d8e8" toneMapped={false} />
            </mesh>
          ))}
          {/* Kolommen en trap */}
          {([[-4, -2.6], [4, -2.6], [-4, 2.6], [4, 2.6]] as const).map(([x, z], i) => (
            <mesh key={i} position={[x, 1.55, z]} castShadow>
              <boxGeometry args={[0.24, 3.1, 0.24]} />
              <meshStandardMaterial color="#454c56" roughness={0.55} metalness={0.5} />
            </mesh>
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <mesh key={i} position={[5.2, 0.3 + i * 0.3, 1.6 - i * 0.28]}>
              <boxGeometry args={[1.3, 0.05, 0.26]} />
              <meshStandardMaterial color="#5c646f" roughness={0.6} metalness={0.5} />
            </mesh>
          ))}
        </group>
      )}

      {/* ---- Veiligheidsspiegels aan de gangpadkoppen ---- */}
      {obj.spiegels &&
        Array.from({ length: ind.cfg.gangpaden }).map((_, i) => (
          <group key={i} position={[-HAL.breedte * 0.44, 3.4, ind.gangpadZ(i)]} rotation={[0, 0.7, 0]}>
            <mesh>
              <sphereGeometry args={[0.42, 16, 12, 0, Math.PI]} />
              <meshStandardMaterial color="#aebdcc" roughness={0.08} metalness={0.95} side={THREE.DoubleSide} />
            </mesh>
            <mesh position={[0, -0.5, 0]}>
              <cylinderGeometry args={[0.03, 0.03, 0.6, 6]} />
              <meshStandardMaterial color="#3e454f" metalness={0.6} roughness={0.5} />
            </mesh>
          </group>
        ))}

      {/* ---- Displayborden met verspringende balken ---- */}
      {obj.displayborden && (
        <group ref={displays}>
          {([[-14, kruising + 1], [8, kruising + 1], [24, -kruising - 1]] as const).map(([x, z], i) => (
            <group key={i} position={[x, 2.6, z]}>
              <mesh>
                <boxGeometry args={[2.2, 1.0, 0.1]} />
                <meshStandardMaterial color="#12161c" roughness={0.5} />
              </mesh>
              <mesh position={[0, 0, 0.06]}>
                <planeGeometry args={[1.8, 0.2]} />
                <meshBasicMaterial color={tokens.data} toneMapped={false} />
              </mesh>
              <mesh position={[0, 0.3, 0.06]}>
                <planeGeometry args={[1.8, 0.1]} />
                <meshBasicMaterial color="#3a4654" toneMapped={false} />
              </mesh>
            </group>
          ))}
        </group>
      )}

      {/* ---- Stillages / rolkooien ---- */}
      {obj.stillages &&
        cijfers.map((c, i) => (
          <group key={i} position={[-18 + i * 9, 0, kruising + 2.5]} rotation={[0, (c % 7) * 0.2, 0]}>
            {/* Onderbak */}
            <mesh position={[0, 0.3, 0]} castShadow>
              <boxGeometry args={[1.2, 0.5, 0.8]} />
              <meshStandardMaterial color="#5a6470" roughness={0.6} metalness={0.5} />
            </mesh>
            {/* Gaaszijden */}
            {([[0, 0.42], [0, -0.42]] as const).map(([x, z], j) => (
              <mesh key={j} position={[x, 0.85, z]}>
                <boxGeometry args={[1.18, 0.62, 0.02]} />
                <meshStandardMaterial color="#6e7885" roughness={0.6} metalness={0.5} wireframe />
              </mesh>
            ))}
            <mesh position={[0, 0.06, 0]}>
              <boxGeometry args={[1.22, 0.1, 0.84]} />
              <meshStandardMaterial color="#3b424b" roughness={0.6} metalness={0.5} />
            </mesh>
          </group>
        ))}

      {/* ---- C-MATIC-palletpendel over het dwarspad ---- */}
      {machines && obj.heftruck && <Palletpendel />}

      {/* ---- Schrobmachine die rondjes rijdt ----
          Een opzitmachine zonder merk (21 oktober): grijze kap met afgeronde
          randen, stoel en stuur, borstelkap voor, rubberen zuigmond achter.
          Eerst een blauw blok — dat las als speelgoed, en sinds er een tegel
          Tennant is, rijdt hij in het beeld van dat onderdeel. */}
      {machines && obj.schrobmachine && (
        <group ref={schrobber} position={[14, 0, kruising + 4]}>
          <RoundedBox args={[0.9, 0.62, 1.25]} radius={0.08} smoothness={3} position={[0, 0.47, 0]} castShadow receiveShadow>
            <meshStandardMaterial color="#6b7076" roughness={0.5} metalness={0.05} />
          </RoundedBox>
          {/* Stootrand rondom */}
          <mesh position={[0, 0.16, 0]} castShadow>
            <boxGeometry args={[0.96, 0.07, 1.31]} />
            <meshStandardMaterial color="#1e2024" roughness={0.75} />
          </mesh>
          {/* Stoel en rugleuning */}
          <mesh position={[0, 0.84, -0.22]} castShadow>
            <boxGeometry args={[0.42, 0.07, 0.38]} />
            <meshStandardMaterial color="#1e2024" roughness={0.7} />
          </mesh>
          <mesh position={[0, 1.02, -0.42]} rotation={[-0.12, 0, 0]} castShadow>
            <boxGeometry args={[0.42, 0.32, 0.07]} />
            <meshStandardMaterial color="#1e2024" roughness={0.7} />
          </mesh>
          {/* Stuurkolom en stuur */}
          <mesh position={[0, 0.98, 0.34]} rotation={[-0.35, 0, 0]} castShadow>
            <cylinderGeometry args={[0.024, 0.03, 0.34, 10]} />
            <meshStandardMaterial color="#2a2d32" roughness={0.5} metalness={0.3} />
          </mesh>
          <mesh position={[0, 1.15, 0.28]} rotation={[Math.PI / 2 - 0.5, 0, 0]} castShadow>
            <torusGeometry args={[0.15, 0.018, 8, 24]} />
            <meshStandardMaterial color="#141519" roughness={0.6} />
          </mesh>
          {/* Borstelkap onder de neus */}
          <mesh position={[0, 0.07, 0.42]}>
            <cylinderGeometry args={[0.43, 0.43, 0.1, 24]} />
            <meshStandardMaterial color="#26282c" roughness={0.7} />
          </mesh>
          {/* Zuigmond: een rubberen strip achter, breder dan de machine */}
          <mesh position={[0, 0.03, -0.75]}>
            <boxGeometry args={[1.0, 0.05, 0.06]} />
            <meshStandardMaterial color="#141519" roughness={0.85} />
          </mesh>
          {/* Nat spoor achter de zuigmond: glad, geen metaalglans, uitlopend */}
          <mesh position={[0, 0.006, -1.9]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[0.98, 2.2]} />
            <meshStandardMaterial
              color="#1a2028" roughness={0.08} metalness={0} transparent opacity={0.45}
              alphaMap={natSpoor} depthWrite={false}
            />
          </mesh>
          <Contactschaduw breedte={0.9} lengte={1.4} />
        </group>
      )}

      {/* ---- Draaiend zwaailicht bij de kruising ---- */}
      <group position={[0, 3.6, kruising]}>
        <mesh>
          <cylinderGeometry args={[0.14, 0.14, 0.22, 12]} />
          <meshStandardMaterial color={tokens.hi} emissive={tokens.hi} emissiveIntensity={0.6} roughness={0.4} />
        </mesh>
        <mesh ref={zwaailicht}>
          <coneGeometry args={[0.55, 2.2, 12, 1, true]} />
          <meshBasicMaterial
            color={tokens.hi} transparent opacity={0.035}
            side={THREE.DoubleSide} blending={THREE.AdditiveBlending}
            depthWrite={false} toneMapped={false}
          />
        </mesh>
      </group>
    </group>
  )
}
